import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AppointmentDoc, ClientDoc } from '../../shared/api/converters.js';
import { keys } from '../../shared/api/keys.js';
import {
  createClient,
  deleteClient,
  subscribeClient,
  subscribeClientAppointments,
  subscribeClients,
  revokeClientConsent,
  updateClient,
  type NewClientInput,
} from '../../shared/api/clients.js';
import { useLiveQuery } from '../../shared/api/useLiveQuery.js';

export function useClients(businessId: string) {
  return useLiveQuery<ClientDoc[]>(keys.clients(businessId), (cb) => subscribeClients(businessId, cb));
}

export function useClient(businessId: string, e164: string) {
  return useLiveQuery<ClientDoc | null>(keys.client(businessId, e164), (cb) =>
    subscribeClient(businessId, e164, cb),
  );
}

export function useClientAppointments(businessId: string, e164: string) {
  return useLiveQuery<AppointmentDoc[]>(keys.clientAppointments(businessId, e164), (cb) =>
    subscribeClientAppointments(businessId, e164, cb),
  );
}

export function useCreateClient(businessId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: NewClientInput) => createClient(businessId, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.clients(businessId) }),
  });
}

export function useUpdateClient(businessId: string, e164: string) {
  return useMutation({
    mutationFn: (patch: { name?: string; notes?: string }) => updateClient(businessId, e164, patch),
  });
}

export function useDeleteClient(businessId: string) {
  return useMutation({ mutationFn: (e164: string) => deleteClient(businessId, e164) });
}

/** Owner-side opt-out (doc 21 §5). Revoke only — an owner cannot consent for
 * their customer. */
export function useRevokeClientConsent(businessId: string, e164: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => revokeClientConsent(businessId, e164),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.clients(businessId) }),
  });
}
